import type { Map as CarteGL } from 'maplibre-gl';
import { nomImageTrame, type TrameEtat } from './trames-etat';

/**
 * LE DESSIN DES TRAMES — la moitié DOM de `trames-etat.ts`, SÉPARÉE EXPRÈS.
 *
 * ⚠️ La table des trames est lue par un BANC (tests/les-trames-disent-l-etat),
 * et `tsconfig.tests.json` n'a pas la lib DOM : `document` dans le même module
 * que la table a fait rougir le PRÉ-VOL du déployeur (11/10/2026) là où mes
 * tsc locaux passaient. La donnée pure et le pinceau ne voyagent pas ensemble.
 */

/**
 * Dessine une trame en pixels, pour `map.addImage`.
 *
 * ⚠️ L'ENCRE EST CELLE DU THÈME, passée en argument : une trame générée avec
 * une encre figée serait invisible sur l'un des deux fonds. Le pas de 12 px
 * est choisi pour rester lisible de z14 à z18 sans scintiller.
 */
export function imageTrame(
  t: Exclude<TrameEtat, 'plein'>, encre: string,
): { width: number; height: number; data: Uint8Array } {
  const N = 12;
  const toile = document.createElement('canvas');
  toile.width = N; toile.height = N;
  const ctx = toile.getContext('2d')!;
  ctx.clearRect(0, 0, N, N);
  ctx.strokeStyle = encre; ctx.fillStyle = encre;
  ctx.lineWidth = 1.5;
  if (t === 'hachures' || t === 'croisillons') {
    ctx.beginPath();
    // Diagonale montante, répétée aux coins pour un raccord sans couture.
    ctx.moveTo(-2, N + 2); ctx.lineTo(N + 2, -2);
    ctx.stroke();
  }
  if (t === 'croisillons') {
    ctx.beginPath();
    ctx.moveTo(-2, -2); ctx.lineTo(N + 2, N + 2);
    ctx.stroke();
  }
  if (t === 'points') {
    ctx.beginPath(); ctx.arc(N / 2, N / 2, 1.2, 0, 2 * Math.PI); ctx.fill();
  }
  const d = ctx.getImageData(0, 0, N, N);
  return { width: N, height: N, data: new Uint8Array(d.data.buffer) };
}

/**
 * Pose les trois images de trame sur la carte — idempotent.
 * ⚠️ CHAQUE TRAME PORTE L'ENCRE DE SON ÉTAT : la trame renforce la teinte,
 * et reste lisible seule quand la teinte disparaît.
 */
export function poserImagesTrames(
  c: CarteGL, encres: Record<Exclude<TrameEtat, 'plein'>, string>,
): void {
  for (const t of ['hachures', 'croisillons', 'points'] as const) {
    const nom = nomImageTrame(t);
    if (!c.hasImage(nom)) c.addImage(nom, imageTrame(t, encres[t]));
  }
}
